import { useEffect, useRef, useState } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Crop as CropIcon } from "lucide-react";
import { Modal } from "@/components/panel/ui";
import { Button } from "@/components/ui-kit";
import { cn } from "@/lib/utils";

/**
 * Recorte de foto antes de entrar no cadastro.
 *
 * A foto que o fornecedor manda raramente é a foto que a loja precisa: vem com
 * fundo sobrando, com dois produtos no mesmo quadro, ou com a tabela de medidas
 * grudada embaixo. Sem recorte, ou a pessoa abria um editor de imagem à parte
 * — e não abria — ou a loja mostrava a foto como veio.
 *
 * A pessoa arrasta um retângulo sobre a foto (ou aceita a foto inteira, que é
 * um botão e não a falta de um). O recorte acontece AQUI, no navegador, na
 * resolução original: o que sobe para o servidor já é o pedaço escolhido, e o
 * arquivo antigo nunca é tocado — recortar uma foto já cadastrada sobe um
 * arquivo novo e troca a referência.
 *
 * "Quadrado" existe porque a vitrine e a página do produto mostram a foto num
 * quadro 1:1: um recorte quadrado preenche o quadro sem faixa branca dos lados.
 * Livre é o padrão porque a maioria das fotos de produto NÃO é quadrada e
 * forçar a proporção cortaria a bengala ao meio.
 *
 * `onDone(File | null)`: `null` significa "usar inteira" — quem chama decide o
 * que fazer (subir o original, ou nada, se a foto já estava cadastrada).
 */
export default function ImageCropDialog({ src, fileName = "foto", mimeType = "image/jpeg", onDone, onCancel }) {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();          // em %, para não depender do tamanho da tela
  const [aspect, setAspect] = useState(undefined); // undefined = livre; 1 = quadrado
  const [pronto, setPronto] = useState(false);
  const [cortando, setCortando] = useState(false);

  /* Recorte inicial: 90% da foto, centralizado. Existe para o botão "Cortar"
     nunca estar morto — sem um recorte de partida a pessoa clicava, nada
     acontecia, e não havia pista de que era preciso arrastar primeiro. */
  const iniciar = (w, h, asp) => {
    const base = makeAspectCrop({ unit: "%", width: 90 }, asp || w / h, w, h);
    setCrop(centerCrop(base, w, h));
  };

  const aoCarregar = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    iniciar(w, h, aspect);
    setPronto(true);
  };

  const trocarProporcao = (asp) => {
    setAspect(asp);
    const img = imgRef.current;
    if (img?.naturalWidth) iniciar(img.naturalWidth, img.naturalHeight, asp);
  };

  // Esc fecha sem aplicar, como todo diálogo do painel.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const aplicar = async () => {
    const img = imgRef.current;
    if (!img || !crop) return;
    setCortando(true);
    try {
      onDone(await recortar(img, crop, fileName, mimeType));
    } finally {
      setCortando(false);
    }
  };

  return (
    <Modal
      open
      onClose={onCancel}
      title="Recortar foto"
      maxWidth="max-w-3xl"
      testId="image-crop-dialog"
      footer={(
        <div className="flex flex-wrap items-center gap-2 w-full">
          <div className="flex items-center gap-1 mr-auto" role="group" aria-label="Proporção do recorte">
            <Proporcao ativa={aspect === undefined} onClick={() => trocarProporcao(undefined)}>Livre</Proporcao>
            <Proporcao ativa={aspect === 1} onClick={() => trocarProporcao(1)}>Quadrado</Proporcao>
          </div>
          <Button variant="ghost" onClick={onCancel} data-testid="image-crop-cancel">Cancelar</Button>
          <Button variant="secondary" onClick={() => onDone(null)} data-testid="image-crop-whole">Usar inteira</Button>
          <Button icon={CropIcon} onClick={aplicar} loading={cortando} disabled={!pronto} data-testid="image-crop-apply">
            Cortar e usar
          </Button>
        </div>
      )}
    >
      <p className="text-sm text-slate-500 mb-3">
        Arraste as bordas para escolher a parte da foto que vai para a loja. Se a foto já está boa, use inteira.
      </p>
      {/* A foto INTEIRA precisa caber na tela — é sobre ela que se escolhe o
          recorte. O limite de altura vai no `.ReactCrop`, e não no <img>: o CSS
          do componente põe `max-height: inherit` no wrapper e na imagem (com
          especificidade maior que uma classe solta), então só o que estiver no
          pai chega até ela. Com o limite na imagem, ela renderizava na altura
          natural e a moldura cortava o pé — a pessoa tinha que dar zoom
          negativo no navegador para ver a foto toda.

          A conta: o diálogo tem 90vh; título, instrução, rodapé e respiros
          somam ~15rem. O que sobra é da foto, sem rolagem e sem corte. */}
      <div className="flex justify-center bg-slate-100 rounded-lg">
        <ReactCrop
          crop={crop}
          onChange={(_, pct) => setCrop(pct)}
          aspect={aspect}
          keepSelection
          className="max-h-[calc(90vh-15rem)]"
        >
          {/* `crossOrigin` porque a foto já cadastrada vem do domínio da API:
              sem ele o canvas fica "contaminado" e o recorte não sai. */}
          <img
            ref={imgRef}
            src={src}
            alt=""
            crossOrigin="anonymous"
            onLoad={aoCarregar}
            className="object-contain select-none"
            data-testid="image-crop-source"
          />
        </ReactCrop>
      </div>
    </Modal>
  );
}

function Proporcao({ ativa, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={ativa}
      data-ring=""
      className={cn(
        "h-8 px-3 rounded-lg text-sm font-medium transition-colors",
        ativa ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
      )}
    >
      {children}
    </button>
  );
}

/**
 * Desenha o pedaço escolhido num canvas na resolução ORIGINAL e devolve um File.
 *
 * O recorte vem em % do que está na tela; multiplicar pelo tamanho natural é o
 * que evita subir uma foto de 600px quando a original tinha 3000 — a loja
 * amplia a capa e a diferença aparece.
 *
 * PNG continua PNG (transparência); o resto vira JPEG a 92%, que é o que a
 * loja serve de qualquer forma.
 */
async function recortar(img, crop, fileName, mimeType) {
  const { sx, sy, sw, sh } = recorteEmPixels(crop, img.naturalWidth, img.naturalHeight);

  const canvas = document.createElement("canvas");
  canvas.width = sw;
  canvas.height = sh;
  canvas.getContext("2d").drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

  const tipo = mimeType === "image/png" ? "image/png" : "image/jpeg";
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, tipo, 0.92));
  const base = fileName.replace(/\.[a-z0-9]+$/i, "") || "foto";
  return new File([blob], `${base}-recorte.${tipo === "image/png" ? "png" : "jpg"}`, { type: tipo });
}

/**
 * A conta do recorte, separada para ser testável sem canvas (o jsdom não tem).
 * Percentual → pixels da foto ORIGINAL, com piso de 1px: um recorte de largura
 * zero faria o canvas lançar erro em vez de devolver uma foto.
 */
export function recorteEmPixels(crop, naturalWidth, naturalHeight) {
  return {
    sx: (crop.x / 100) * naturalWidth,
    sy: (crop.y / 100) * naturalHeight,
    sw: Math.max(1, Math.round((crop.width / 100) * naturalWidth)),
    sh: Math.max(1, Math.round((crop.height / 100) * naturalHeight)),
  };
}
